import React, { useState, useEffect, useRef } from 'react';
import { searchAPI } from '../lib/api';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Search, 
  BookOpen, 
  GraduationCap, 
  ClipboardCheck,
  X,
  ArrowRight,
  FileText,
  Sparkles
} from 'lucide-react';
import { Link } from 'react-router-dom';

const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < 2) {
      setResults(null);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await searchAPI.search(query);
        setResults(response.data);
        setIsOpen(true);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const handleResultClick = () => {
    setIsOpen(false);
    setQuery('');
    setResults(null);
  };

  const highlightText = (text, searchTerms) => {
    if (!text || !searchTerms) return text;
    const terms = searchTerms.toLowerCase().split(' ');
    let result = text;
    terms.forEach(term => {
      if (term.length > 1) {
        const regex = new RegExp(`(${term})`, 'gi');
        result = result.replace(regex, '<mark class="bg-amber-200 px-0.5 rounded">$1</mark>');
      }
    });
    return result;
  };

  const totalResults = results?.total_results || 0;

  return (
    <div className="relative" ref={searchRef} data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          type="text"
          placeholder="Search SOPs, trainings, transcripts..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results && setIsOpen(true)}
          className="pl-9 pr-9 w-96 bg-slate-50 border-slate-200 focus:bg-white"
          data-testid="global-search-input"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults(null); setIsOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && results && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-200 z-50 max-h-[500px] overflow-y-auto" data-testid="search-results">
          {loading ? (
            <div className="p-4 text-center text-slate-500">
              <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto" />
            </div>
          ) : totalResults === 0 ? (
            <div className="p-6 text-center text-slate-500">
              <Search className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm font-medium">No results found for "{query}"</p>
              <p className="text-xs text-slate-400 mt-1">Try different keywords or check spelling</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {/* Search Summary */}
              <div className="p-3 bg-slate-50 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span className="text-sm text-slate-600">
                  Found <strong>{totalResults}</strong> results for "<strong>{results.query}</strong>"
                </span>
              </div>

              {/* SOPs */}
              {results.sops?.length > 0 && (
                <div className="p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">SOPs</span>
                    <Badge variant="outline" className="text-xs">{results.sops.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {results.sops.slice(0, 5).map((sop) => (
                      <Link 
                        key={sop.id} 
                        to="/sops" 
                        onClick={handleResultClick}
                        className="block p-3 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {sop.sop_number && (
                                <Badge variant="outline" className="text-xs font-mono">{sop.sop_number}</Badge>
                              )}
                              <p 
                                className="text-sm font-medium text-slate-900 truncate"
                                dangerouslySetInnerHTML={{ __html: highlightText(sop.title, query) }}
                              />
                            </div>
                            {sop._snippet && (
                              <p 
                                className="text-xs text-slate-500 line-clamp-2"
                                dangerouslySetInnerHTML={{ __html: highlightText(sop._snippet, query) }}
                              />
                            )}
                            {sop._matched_in?.length > 0 && (
                              <div className="flex items-center gap-1 mt-1">
                                <span className="text-xs text-slate-400">Found in:</span>
                                {sop._matched_in.map(field => (
                                  <Badge key={field} className="text-xs bg-emerald-50 text-emerald-700 px-1 py-0">
                                    {field}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Trainings */}
              {results.trainings?.length > 0 && (
                <div className="p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <GraduationCap className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Training Videos</span>
                    <Badge variant="outline" className="text-xs">{results.trainings.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {results.trainings.slice(0, 5).map((training) => (
                      <Link 
                        key={training.id} 
                        to="/trainings" 
                        onClick={handleResultClick}
                        className="block p-3 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p 
                              className="text-sm font-medium text-slate-900"
                              dangerouslySetInnerHTML={{ __html: highlightText(training.label, query) }}
                            />
                            {training._snippet && (
                              <p 
                                className="text-xs text-slate-500 line-clamp-2 mt-1"
                                dangerouslySetInnerHTML={{ __html: highlightText(training._snippet, query) }}
                              />
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-slate-400">{training.category}</span>
                              {training._matched_in?.includes('transcript') && (
                                <Badge className="text-xs bg-blue-50 text-blue-700 px-1 py-0">
                                  <FileText className="w-3 h-3 mr-1" />
                                  transcript
                                </Badge>
                              )}
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Onboarding Items */}
              {results.onboarding_items?.length > 0 && (
                <div className="p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <ClipboardCheck className="w-4 h-4 text-amber-600" />
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Checklist Items</span>
                    <Badge variant="outline" className="text-xs">{results.onboarding_items.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {results.onboarding_items.slice(0, 5).map((item) => (
                      <Link 
                        key={item.id} 
                        to="/onboarding" 
                        onClick={handleResultClick}
                        className="block p-3 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p 
                              className="text-sm font-medium text-slate-900"
                              dangerouslySetInnerHTML={{ __html: highlightText(item.title, query) }}
                            />
                            {item._snippet && (
                              <p 
                                className="text-xs text-slate-500 line-clamp-2 mt-1"
                                dangerouslySetInnerHTML={{ __html: highlightText(item._snippet, query) }}
                              />
                            )}
                            <span className="text-xs text-slate-400">{item.category}</span>
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
